<template>
    <div class="w-[550px] rounded-md border border-gray-200 bg-white p-4">
        <div class="mb-4">
            <n-input size="small" v-model:value="pattern" placeholder="Search documents..." clearable />
        </div>
        <div class="h-80 overflow-y-auto">
            <div class="space-y-2">
                <!-- Multiple selection mode with checkboxes -->
                <n-checkbox-group v-if="multiple" v-model:value="selectedDocumentIds">
                    <div
                        v-for="document in documents"
                        :key="document.documentId"
                        class="flex cursor-pointer items-center gap-3 rounded border border-gray-100 p-2 hover:bg-gray-50"
                        @click="toggleDocument(document)"
                    >
                        <n-checkbox :value="document.documentId" @click.stop />
                        <i :class="getMimeTypeIcon(document.mimeType)"></i>
                        <div class="min-w-0 flex-1">
                            <p class="truncate text-wrap text-sm font-medium text-gray-900">
                                {{ document.documentId }} - {{ documentTranslationsMap[document.documentType as DocumentType] || document.documentType }} -
                                {{ document.filename }}
                            </p>
                        </div>
                    </div>
                </n-checkbox-group>

                <!-- Single selection mode with radio buttons -->
                <n-radio-group v-else v-model:value="selectedDocumentId">
                    <div
                        v-for="document in documents"
                        :key="document.documentId"
                        class="flex cursor-pointer items-center gap-3 rounded border border-gray-100 p-2 hover:bg-gray-50"
                        @click="selectDocument(document)"
                    >
                        <n-radio :value="document.documentId" @click.stop />
                        <i :class="getMimeTypeIcon(document.mimeType)"></i>
                        <div class="min-w-0 flex-1">
                            <p class="truncate text-wrap text-sm font-medium text-gray-900">
                                {{ document.documentId }} - {{ documentTranslationsMap[document.documentType as DocumentType] || document.documentType }} -
                                {{ document.filename }}
                            </p>
                        </div>
                    </div>
                </n-radio-group>

                <div v-if="documents.length === 0" class="py-8 text-center text-gray-500">No documents found</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { NInput, NCheckbox, NCheckboxGroup, NRadio, NRadioGroup } from 'naive-ui';
import { ref, computed, watch } from 'vue';
import { type IDocument, type DocumentType, type IFindDocumentsReq } from '@condo/domain';
import { getMimeTypeIcon } from '@/shared';
import { documentTranslationsMap } from '@condo/domain';
import { useDocuments } from '@/hooks/documents.hooks';

export interface Props {
    mfhId?: number;
    mfhIds?: number[];
    estateId?: number;
    documents?: IDocument[];
    multiple?: boolean;
    mimeType?: string;
    documentTypes?: DocumentType[];
    limit?: number;
}

export interface Emits {
    (ev: 'update:documents', value: IDocument[]): void;
}

const props = withDefaults(defineProps<Props>(), {
    multiple: false,
});

const emit = defineEmits<Emits>();

const pattern = ref('');
const selectedDocuments = ref<IDocument[]>([]);

const queryParams = computed<IFindDocumentsReq>(() => ({
    query: {
        ...(props.mfhIds ? { mfhIds: props.mfhIds } : props.mfhId ? { mfhIds: [props.mfhId] } : {}),
        ...(props.estateId ? { estateIds: [props.estateId] } : {}),
        ...(props.documentTypes ? { documentTypes: props.documentTypes } : {}),
        ...(props.mimeType ? { mimeType: props.mimeType } : {}),
        ...(props.limit ? { limit: props.limit } : {}),
        ...(pattern.value ? { fileName: pattern.value } : {}),
    },
    expand: {
        expand: {
            document: {
                select: ['documentId', 'documentType', 'uuid', 'filename', 'link', 'path', 'mimeType'],
            },
        },
    },
}));

const { data: documents } = useDocuments(queryParams);

// For multiple selection: array of document IDs
const selectedDocumentIds = computed({
    get: () => selectedDocuments.value.map(doc => doc.documentId!),
    set: (ids: number[]) => {
        const newSelected = documents.value.filter(doc => ids.includes(doc.documentId!));
        selectedDocuments.value = newSelected;
        emit('update:documents', newSelected);
    },
});

// For single selection: single document ID
const selectedDocumentId = computed({
    get: () => selectedDocuments.value[0]?.documentId ?? null,
    set: (id: number | null) => {
        if (id === null) {
            selectedDocuments.value = [];
            emit('update:documents', []);
        } else {
            const document = documents.value.find(doc => doc.documentId === id);
            if (document) {
                selectedDocuments.value = [document];
                emit('update:documents', [document]);
            }
        }
    },
});

const toggleDocument = (document: IDocument) => {
    if (!props.multiple) return;

    const currentIds = selectedDocumentIds.value;
    const documentId = document.documentId!;
    const index = currentIds.indexOf(documentId);

    if (index > -1) {
        selectedDocumentIds.value = currentIds.filter(id => id !== documentId);
    } else {
        selectedDocumentIds.value = [...currentIds, documentId];
    }
};

const selectDocument = (document: IDocument) => {
    if (props.multiple) return;
    selectedDocumentId.value = document.documentId!;
};

watch(
    () => props.documents,
    newDocs => {
        selectedDocuments.value = newDocs ? structuredClone(newDocs) : [];
    },
    { immediate: true, deep: true },
);
</script>

<style scoped></style>
